import type React from 'react';

import { Button, Image, Spinner, Text } from '@change/ds-core-ui';
import { FacebookRegularIcon as FacebookIcon } from '@change/ds-icons';

import { useMarkModuleAsLoaded, useReadyToShareContext } from 'src/app/pages/starterDashboardV2/shared/contexts';
import type { WaypointCallbackArgs } from 'src/app/shared/components/layout/lazy';
import { Waypoint } from 'src/app/shared/components/scroll';

import { SharePreviewModal } from './components/SharePreviewModal';

const FACEBOOK_POSTING_IMAGE = 'https://static.change.org/images/pre-made-post-icon-reskin.svg';
export const ReadyToShare: React.FC = () => {
	const {
		data,
		actions: { closeSharePreviewModal, openSharePreviewModal, trackView },
	} = useReadyToShareContext();

	useMarkModuleAsLoaded('ReadyToShare', data.shouldShowReadyToShareExperiment);

	if (!data.shouldShowReadyToShareExperiment) {
		return null;
	}

	if (data.isLoading) {
		return <Spinner className="mx-auto my-16" data-testid="ready-to-share-loading" />;
	}

	return (
		<section className="bg-background-base flex h-full w-full px-4 py-8 md:px-12 md:pt-8 md:pb-12">
			<Waypoint onPositionChange={(wp: WaypointCallbackArgs) => trackView(wp.currentPosition)} />
			<div className="flex w-full flex-col-reverse gap-8 md:min-h-[169px] md:flex-row md:gap-10">
				<div className="flex h-full flex-col items-start justify-center gap-5">
					<div className="flex flex-col gap-2 md:gap-3">
						<Text className="text-size-heading-xs md:text-size-heading-small">
							A custom Facebook post made just for your petition
						</Text>
						<Text className="text-size-body">
							We wrote a Facebook post to help your petition get more signatures. Share it on your feed or in groups.
						</Text>
					</div>
					<Button
						data-testid="see-post-button"
						variant="secondary-solid"
						className="w-full md:w-auto"
						size="small"
						onClick={() => openSharePreviewModal()}
					>
						View & share your post
					</Button>
				</div>
				<div className="bg-background-overlay relative flex w-full items-center justify-center rounded-tl-lg rounded-tr-[22px] rounded-br-lg rounded-bl-lg px-12 py-2.5 md:w-[166px] md:grow">
					<div className="bg-background-base absolute top-0 right-0 flex h-12 w-12 items-center justify-center rounded-full text-[#1877F2] md:h-8 md:w-8">
						<FacebookIcon className="h-full w-full" svgClassName="w-full h-full" />
					</div>
					<div className="flex h-full w-full items-center justify-center">
						<Image
							src={FACEBOOK_POSTING_IMAGE}
							alt="Mobile skeleton of a Facebook post"
							className="min-h-[213px] min-w-[100px] object-contain md:min-h-[149px] md:min-w-[70px]"
							onClick={() => openSharePreviewModal()}
							data-testid="facebook-post-image"
						/>
					</div>
				</div>
			</div>
			{data.readyToSharePostsValue && data.readyToSharePostsValue.length > 0 && (
				<SharePreviewModal
					isOpen={data.isOpen}
					onClose={closeSharePreviewModal}
					readyToSharePost={data.readyToSharePostsValue[0]}
				/>
			)}
		</section>
	);
};
